// The Overview's "Issue Status Breakdown" card's data — IA_ABSORPTION_PLAN Phase 4 step 14. Split out
// of the store-coupled `components/cards/IssueStatusBarCard.vue` — since 2026-10-12
// `@rdrive/plugin-total-cards`' StatusBreakdownCard (ProjectPage passes the title) — which only
// renders what this returns (props in, no store, no SQL). The query, the row shaping and the two reload triggers are
// the old component's, verbatim, but for:
//   - each item's `id` is its group's key (title + colour, the GROUP BY): the query selects no status
//     id, so every `id` was undefined and the legend's hover highlight never dimmed the other
//     segments (`focusedItemId === item.id` held for all of them);
//   - the colourless fallback is the `@rdrive/theme` token `chart.statusBreakdown.fallback`
//     (the same `#9E9E9E`);
//   - "DuckDB initialised" is not checked beside `dataLoaded`: the data loader only flips
//     `dataLoaded` after it has loaded the tables INTO DuckDB, so the flag implies it.
import { ref, unref, watch } from 'vue';
import { tokens } from '@rdrive/theme';

/**
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn  DuckDB connection (DuckDBService's gated `conn`)
 * @param {import('vue').Ref<object>} deps.filterContext  the issues filter context (its
 *   `filteredIssues` view; reloads on its `filtersLastUpdated` tick)
 * @param {import('vue').Ref<boolean>} deps.dataLoaded  the global data-loaded flag
 * @returns {{ items: import('vue').Ref<Array<{ id: string, title: string, color: string,
 *   count: number, value: number }>>, loading: import('vue').Ref<boolean> }}
 *   `value` is the status's share of the issues in percent, one decimal
 */
export function useIssueStatusBreakdown({ conn, filterContext, dataLoaded }) {
  const items = ref([]);
  const loading = ref(true);

  async function fetchStatusBreakdown() {
    try {
      loading.value = true;

      // Query to get status breakdown from filtered issues
      const query = `
      SELECT
        TRIM(st.Title) as statusTitle,
        TRIM(st.Color) as statusColor,
        MIN(st.Sort) as statusSort,
        COUNT(fi.id) as issueCount
      FROM filteredIssues fi
      LEFT JOIN status_templates st ON fi.issueStatusId = st.Id
      WHERE st.Title IS NOT NULL
      GROUP BY TRIM(st.Title), TRIM(st.Color)
      ORDER BY MIN(st.Sort)
    `;

      const results = await unref(conn).query(query);

      const data = results.toArray().map((row) => {
        const jsonRow = row.toJSON();
        return {
          ...jsonRow,
          sort: Number(jsonRow.statusSort),
          issueCount: Number(jsonRow.issueCount) || 0,
        };
      });

      // Calculate total issues
      const totalIssues = data.reduce((sum, row) => sum + row.issueCount, 0);

      // Transform data into items with percentages
      items.value = data.map((row) => ({
        id: `${row.statusTitle}|${row.statusColor}`,
        title: row.statusTitle || 'Unknown Status',
        color: row.statusColor || tokens.chart.statusBreakdown.fallback,
        count: row.issueCount,
        value: totalIssues > 0 ? Math.round((row.issueCount / totalIssues) * 1000) / 10 : 0,
      }));
    } catch (error) {
      console.error('Error fetching status breakdown:', error);
      items.value = [];
    } finally {
      loading.value = false;
    }
  }

  // Initial fetch when data is loaded
  watch(
    () => unref(dataLoaded),
    async (loaded) => {
      if (loaded) {
        await fetchStatusBreakdown();
      }
    },
    { immediate: true },
  );

  // Also watch for filter changes
  watch(
    () => unref(filterContext)?.filtersLastUpdated,
    async () => {
      if (unref(dataLoaded)) {
        await fetchStatusBreakdown();
      }
    },
    { deep: true },
  );

  return { items, loading };
}
